import $ from 'jquery';

/** Tables that call `$.fn.DataTable` from PHP inline scripts (server-side / custom config). */
/** `#table-users` is initialized in `partials/control_panel/users.js` to avoid double-loading. */
/**
 * `.dt-server` is the five Reports grids. They are a CLASS rather than ids because the five
 * views are interchangeable and each carries a `data-type`; leaving them out of this list is
 * why every report page logged `r.DataTable is not a function` and sat at "Loading..." - the
 * chunk was never fetched, so the plugin the reports module reaches for did not exist (#764).
 */
const SERVER_SIDE_TABLE_SELECTOR =
    '#table-returns, #table-stock-notifications, #table-lost-sales, #table-orders, #table-records, .dt-server';

/**
 * Sticky readiness contract for legacy inline DataTables init scripts.
 *
 * Many admin views ship inline `<script>` blocks that look like:
 *
 *     $(document).one('datatables:ready', function () {
 *         $('#table-x').DataTable({...});
 *     });
 *
 * That pattern is FRAGILE because `initDataTables()` loads the DataTables
 * chunk via a dynamic `import()`. The order of (a) the inline handler
 * registering on the document and (b) the chunk resolving + firing
 * `datatables:ready` is not guaranteed across browsers / HTTP-2 preload /
 * cached chunks. On dev2 we hit a race where the inline handler ran via
 * jQuery's $(document).ready Deferred BEFORE the import resolved, so
 * `$.fn.DataTable` was still undefined.
 *
 * The fix:
 *   1. Expose `window.dataTablesReady` (Promise) — the canonical, race-free
 *      hook for new code: `window.dataTablesReady.then(() => $('#x').DataTable(...))`.
 *   2. Keep firing `datatables:ready` on document AND replay it whenever a
 *      new handler is registered after load (via `onDataTablesReady`).
 *   3. Expose `window.onDataTablesReady(cb)` — a drop-in for the legacy
 *      `$(document).one('datatables:ready', cb)` calls that runs `cb`
 *      synchronously if DataTables is already loaded, otherwise queues it.
 *
 * NOT a drop-in at the point of CALL, though, and this is the trap: both globals
 * below are set when this module evaluates, and main.js is emitted as
 * `<script type="module">`, which is deferred. An inline view script running
 * during parsing therefore sees neither. `$(document).one(...)` is safe there
 * because binding an event early is fine; `onDataTablesReady` is not, and must be
 * reached from inside `$(document).ready()`. Calling it at the top level of an
 * inline block throws, and the throw takes the rest of that block with it.
 */

let dataTablesReadyResolve;
const dataTablesReadyPromise = new Promise(function (resolve) {
    dataTablesReadyResolve = resolve;
});
let dataTablesLoaded = false;

if (typeof window !== 'undefined') {
    window.dataTablesReady = dataTablesReadyPromise;
    window.onDataTablesReady = function (cb) {
        if (typeof cb !== 'function') return;
        if (dataTablesLoaded) {
            try {
                cb();
            } catch (err) {
                console.error('[DataTables] handler error', err);
            }
            return;
        }
        dataTablesReadyPromise.then(cb).catch(function (err) {
            console.error('[DataTables] handler error', err);
        });
    };
}

export function initDataTables() {
    const needsPlugin = $('.dt-table').length || $('.dt-exportable').length || $(SERVER_SIDE_TABLE_SELECTOR).length;

    if (!needsPlugin) return;

    return import('./vendor/datatables.js')
        .then(function () {
            if ($('.dt-table').length) {
                // Views must leave the tbody empty and declare their wording here:
                // DataTables reads tbody positionally and ignores colspan, so a
                // one-cell placeholder row is a short row and it warns.
                $('.dt-table').each(function () {
                    var options = { responsive: true };
                    var emptyMessage = $(this).data('empty-message');

                    if (emptyMessage) {
                        options.language = { emptyTable: emptyMessage };
                    }

                    $(this).DataTable(options);
                });

                // No manual class fixing here. datatables.net-bs5 already sets
                // length.select = 'form-select form-select-sm' and
                // search.input  = 'form-control form-control-sm'.
                // Adding form-control to the SELECT on top of that was the bug: Bootstrap's
                // .form-control and .form-select both set padding and background, and
                // .form-control wins on the padding-right that .form-select reserves for its
                // chevron - so the arrow rendered on top of the number (visible as "10v" in the
                // entries-per-page control on every dt-table screen).
                //
                // The old selectors were also hardcoded to DataTables_Table_0, so on a page with
                // two tables only the first was ever touched.
            }

            if ($('.dt-exportable').length) {
                $('.dt-exportable').DataTable({
                    dom: 'B<"clear">lfrtip',
                    buttons: [
                        {
                            extend: 'excel',
                            exportOptions: { columns: 'thead th:not(.no-export)' },
                        },
                        {
                            extend: 'csv',
                            exportOptions: { columns: 'thead th:not(.no-export)' },
                        },
                        {
                            extend: 'pdf',
                            exportOptions: { columns: 'thead th:not(.no-export)' },
                        },
                    ],
                    responsive: true,
                });

                // No manual class fixing here. datatables.net-bs5 already sets
                // length.select = 'form-select form-select-sm' and
                // search.input  = 'form-control form-control-sm'.
                // Adding form-control to the SELECT on top of that was the bug: Bootstrap's
                // .form-control and .form-select both set padding and background, and
                // .form-control wins on the padding-right that .form-select reserves for its
                // chevron - so the arrow rendered on top of the number (visible as "10v" in the
                // entries-per-page control on every dt-table screen).
                //
                // The old selectors were also hardcoded to DataTables_Table_0, so on a page with
                // two tables only the first was ever touched.
            }

            // Mark loaded + resolve the sticky promise BEFORE firing the legacy
            // event, so handlers using `window.onDataTablesReady()` registered
            // late still see `dataTablesLoaded === true`.
            dataTablesLoaded = true;
            if (typeof dataTablesReadyResolve === 'function') {
                dataTablesReadyResolve();
            }

            // Legacy contract: inline scripts in views may `.one('datatables:ready', ...)`.
            $(document).trigger('datatables:ready');
        })
        .catch(function (err) {
            // Without this, a failed chunk would silently leave admin tables empty.
            console.error('[DataTables] failed to load chunk', err);
        });
}
